import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import Result from "./Result";
import { parseThroughputUsage, parseWireRoles } from "./api.js";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import ThroughputStatusHelp from "./Help";
import { Header } from "common/Header";
import useDebouncer from "common/hooks/debouncer";

const LOAD_INTERVAL = 3000;

const ViewContainer = styled.div`
  min-height: 50vh;
  &.card {
    height: 100%;
  }
`;

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
        <a
          onClick={() => {
            actions.send("do-load");
          }}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const throughputUsageRetrieval = () =>
  ifCl
    .run("show interface pkteng bandwidth")
    .then(parseThroughputUsage);

const deviceRoleRetrieval = () =>
  ifCl.run("show pkteng wire").then(parseWireRoles).catch(() => ({}));

const translateDeviceNames = ([usage, roleByDevice]) =>
  usage.map( ({name, ...use}) => ({
    ...use,
    name: roleByDevice[name] || name,
  }))

const ThroughputStatus = () => {
  const {
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const debounce = useDebouncer(500 /*ms*/);

  useEffect(() => {
    let active = true;
    let deviceRolesReq = null;

    // Roles seldom change: manual loads fetch them fresh, while the
    // background ticks reuse the request cached for this effect run.
    const deviceRolesRetrieval = (fresh) => {
      if (fresh || deviceRolesReq === null) {
        deviceRolesReq = deviceRoleRetrieval();
      }
      return deviceRolesReq;
    };

    const throughputUsageAndDevicesRetrieval = (freshRoles = false) =>
      Promise.all([throughputUsageRetrieval(), deviceRolesRetrieval(freshRoles)])
        .then(translateDeviceNames)
        .then(usage => ({usage}))

    const doLoad = () => {
      const req = throughputUsageAndDevicesRetrieval(/*freshRoles*/ true)
      .then( data=> {
        if (active) setLiveData(data);
        return data;
      });
      setRequest(req);
    };

    const doBackgroundLoad = () => {
      throughputUsageAndDevicesRetrieval()
        .then((data) => {
          if (active) setLiveData(data);
        })
        .catch((err) => console.error("Throughput background fetch failed:", err));
    };

    doLoad();
    const intervalId = setInterval(doBackgroundLoad, LOAD_INTERVAL);
    const unsubscribe = actions.recv("do-load", () => debounce(doLoad));

    return () => {
      active = false;
      clearInterval(intervalId);
      unsubscribe();
      debounce.stop();
    };
  }, [mode, bqnId, debounce]);

  return (
    <ViewContainer className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
      <div className="card">
        <Header
          title="Current Network Interface Throughput"
          contextMenu={<ContextMenu />}
          bqnSelector={true}
        />
        <div className="body flex-panel-column">
          <Request during={request}>
            {() => (liveData ? <Result {...liveData} /> : null)}
          </Request>
        </div>
      </div>
      <ThroughputStatusHelp />
    </ViewContainer>
  );
};

export default ThroughputStatus;
